import {
  AdministracionSnapshot,
  MiembroAdminSnapshot,
  RepresentativeSnapshot,
} from '../../operaciones-societarias-form/models/constitution-snapshot.model';
import { MemberRow } from '../models/member-row';
import {
  formatAdminSystemLabel,
  formatCargoLabel,
  formatClassificationLabel,
  formatRetributionKeyLabels,
  formatSelfEmployedChargedToLabel,
} from './admin-display.util';
import { formatSnapshotDate } from './snapshot-date.util';

function mapRepresentatives(reps: any[] | null | undefined): RepresentativeSnapshot[] {
  return (reps ?? [])
    .map((r: any) => {
      const first = r?.firstSurname ?? r?.first_surname ?? '';
      const second = r?.secondSurname ?? r?.second_surname ?? '';
      const given = r?.name ?? '';
      const fullName = [first, second, given].filter(Boolean).join(' ').trim() || given || '';
      const nif = r?.documentNumber ?? r?.document_number ?? r?.nif ?? '';
      if (!fullName && !nif) return null;
      return { name: fullName, nif };
    })
    .filter((x): x is RepresentativeSnapshot => x != null);
}

function buildMemberSnapshot(
  m: any,
  administrationSystem: string | null | undefined,
): MiembroAdminSnapshot {
  const cargoRaw = m.cargo ?? '';
  const cargoLabel = formatCargoLabel(cargoRaw, m.administrationSystem ?? administrationSystem);
  const retributionKeys = Array.isArray(m.retributionKeys) ? m.retributionKeys : [];

  return {
    nombre: m.name ?? '',
    nif: m.documentNumber ?? '',
    cargo: cargoLabel,
    fechaAlta: formatSnapshotDate(m.cargoStartDate),
    fechaBaja: m.cargoEndDate ? formatSnapshotDate(m.cargoEndDate) : undefined,
    domicilio: m.address,
    associatedClientName: m.associatedClientName ?? m.associatedClient?.name,
    representatives: mapRepresentatives(m.representatives),
    cargoRaw,
    isCeo: m.isCeo ?? false,
    ceoStartDate: m.ceoStartDate ? formatSnapshotDate(m.ceoStartDate) : undefined,
    ceoEndDate: m.ceoEndDate ? formatSnapshotDate(m.ceoEndDate) : undefined,
    hasRetribution: m.hasRetribution ?? false,
    retributionKeys: formatRetributionKeyLabels(retributionKeys),
    retributionAmountA: m.retributionAmountA,
    retributionAmountE: m.retributionAmountE,
    retributionAmountG: m.retributionAmountG,
    inStatutes: m.inStatutes ?? false,
    hasContract: m.hasContract ?? false,
    hasMinutes: m.hasMinutes ?? false,
    retributionStartDate: m.retributionStartDate
      ? formatSnapshotDate(m.retributionStartDate)
      : undefined,
    socialSecurityRegime: m.socialSecurityRegime,
    classification: formatClassificationLabel(m.classification),
    selfEmployedChargedTo: formatSelfEmployedChargedToLabel(m.selfEmployedChargedTo),
    selfEmployedChargedName: m.selfEmployedChargedName,
    iban: m.iban,
    monthlyNetIncome: m.monthlyNetIncome,
    mutualAccidents: m.mutualAccidents,
    mutualCommonContingencies: m.mutualCommonContingencies,
    signsCCAA: m.signsCCAA ?? false,
    certifiesCCAA: m.certifiesCCAA ?? false,
    signsIS: m.signsIS ?? false,
    certifiesIS: m.certifiesIS ?? false,
    signsLaborContracts: m.signsLaborContracts ?? false,
    signsLaborDocuments: m.signsLaborDocuments ?? false,
    observations: m.observations ?? '',
    isCeoLabel: m.isCeo ? '✓' : '',
  };
}

export function mapMemberApiToSnapshot(
  member: any,
  administrationSystem: string | null | undefined,
): MiembroAdminSnapshot {
  return buildMemberSnapshot(member, administrationSystem);
}

export function mapOperationMemberToSnapshot(
  member: any,
  administrationSystem: string | null | undefined,
): MiembroAdminSnapshot {
  return buildMemberSnapshot(member, administrationSystem);
}

export function mapMemberRowToSnapshot(
  member: MemberRow & { representatives?: Array<{ id: number; name: string; nif?: string }> },
  administrationSystem: string | null | undefined,
): MiembroAdminSnapshot {
  const reps: RepresentativeSnapshot[] = (member.representatives ?? []).map(r => ({
    name: r.name,
    nif: r.nif ?? '',
  }));

  return buildMemberSnapshot(
    {
      ...member,
      representatives: reps,
    },
    administrationSystem,
  );
}

export function mapAdministrationToSnapshot(
  admin: any,
): AdministracionSnapshot {
  const sistemaRaw = admin?.administrationSystem ?? '';
  const members: any[] = admin?.members ?? [];
  return {
    sistema: formatAdminSystemLabel(sistemaRaw),
    sistemaRaw,
    miembros: members.map(m => mapOperationMemberToSnapshot(m, sistemaRaw)),
  };
}

/** Loads administration + members from API and maps to snapshot. */
export async function loadAdministrationSnapshotFromApi(
  getAdministrationByClient: (clientId: number) => Promise<any>,
  getMembersByAdministration: (administrationId: number) => Promise<any>,
  clientId: number,
): Promise<AdministracionSnapshot> {
  try {
    const adminRes = await getAdministrationByClient(clientId);
    if (!adminRes?.id) {
      return mapAdministrationApiToSnapshot(adminRes);
    }
    const members = await getMembersByAdministration(adminRes.id);
    return mapAdministrationApiToSnapshot({ ...adminRes, members });
  } catch {
    return { sistema: '', miembros: [] };
  }
}

export function mapAdministrationApiToSnapshot(
  adminRaw: any,
): AdministracionSnapshot {
  const adminArr = Array.isArray(adminRaw) ? adminRaw : adminRaw ? [adminRaw] : [];
  const latestAdmin = adminArr[adminArr.length - 1] ?? null;
  if (!latestAdmin) {
    return { sistema: '', miembros: [] };
  }
  const administrationData = latestAdmin?.administration ?? latestAdmin;
  const adminMembers = Array.isArray(latestAdmin)
    ? latestAdmin
    : latestAdmin?.members ?? latestAdmin?.administrationMembers ?? [];
  const sistemaRaw =
    administrationData?.administrationSystem ??
    adminMembers[0]?.administrationSystem ??
    '';
  return {
    sistema: formatAdminSystemLabel(sistemaRaw),
    sistemaRaw,
    miembros: adminMembers.map((m: any) => mapMemberApiToSnapshot(m, sistemaRaw)),
  };
}
